import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import Swal from 'sweetalert2';
import { lastValueFrom } from 'rxjs';

interface PreguntaAlternativa {
  correcta: number;
}

interface Pregunta {
  alternativas: PreguntaAlternativa[];
}

@Component({
  selector: 'app-create-evaluacion',
  standalone: true,
  imports: [FormsModule, CommonModule, ReactiveFormsModule],
  templateUrl: './create-evaluacion.component.html',
  styleUrl: './create-evaluacion.component.css',
})
export class CreateEvaluacionComponent implements OnInit {
  mostrarEvaluacion = true;
  mostrarInputImagenes = false;
  evaluationForm!: FormGroup;
  areas: any[] = [];
  isFormDirty = false;
  user: any = null;
  id_rol_usuario: string | null = null;
  id_rol: string | null = null;
  id_area_usuario: string | null = null;
  filesToUpload: File[] = [];
  uploadProgress = 0;
  minFiles = 1;
  maxFiles = 8;
  id_evaluacion: string | null = null;
  numPreguntas = 25 ;
  urlConvertidorPdf = 'https://smallpdf.com/es/pdf-a-jpg';
  gradosDisponibles: number[] = [];
  hoy: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private fb: FormBuilder
  ) {
    const hoy = new Date();
    const utc5 = new Date(hoy.getTime() - 5 * 60 * 60 * 1000);
    const fechaUTC5 = utc5.toISOString().split('T')[0];
    this.hoy = fechaUTC5;
  }

  ngOnInit(): void {
    this.loadUserData();
    this.evaluationForm = this.fb.group({
      nombre: ['', [Validators.required, Validators.maxLength(100)]],
      duracion: ['', [Validators.required, Validators.min(1)]],
      etapa: ['', [Validators.required]],
      fecha_inicio: ['', [Validators.required]],
      fecha_fin: ['', [Validators.required]],
      grado: [null, [Validators.required, Validators.min(1)]],
      nivel_educativo: ['', [Validators.required]],
      cantidad_preguntas: [
        25,
        [Validators.required, Validators.min(5), Validators.max(25)],
      ],
      link_imagenes: this.fb.array([]),
      id_area: [null, [Validators.required]],
      preguntas: new FormArray([]),
    });

    this.inicializarPreguntas();
    this.loadAreas();

    if (this.id_rol == '3') {
      this.evaluationForm.get('id_area')?.disable();
      this.evaluationForm.get('id_area')?.setValue(this.id_area_usuario);
    }

    this.evaluationForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });
    this.actualizarGrados();
  }

  actualizarGrados(): void {
    const nivelEducativo = this.evaluationForm.get('nivel_educativo')?.value;
    if (nivelEducativo === 'PRIMARIA') {
      this.gradosDisponibles = Array.from({ length: 6 }, (_, i) => i + 1);
    } else if (nivelEducativo === 'SECUNDARIA') {
      this.gradosDisponibles = Array.from({ length: 5 }, (_, i) => i + 1);
    }

    const gradoActual = this.evaluationForm.get('grado')?.value;
    if (gradoActual && !this.gradosDisponibles.includes(gradoActual)) {
      this.evaluationForm.get('grado')?.setValue(this.gradosDisponibles[0]);
    }
  }

  get preguntasArray(): FormArray {
    return this.evaluationForm.get('preguntas') as FormArray;
  }

  getPreguntaControl(index: number): FormControl {
    return this.preguntasArray.at(index) as FormControl;
  }

  onCheckboxChange(index: number, option: string, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const preguntaControl = this.preguntasArray.at(index) as FormControl;
    const currentValues = preguntaControl.value as string[];

    if (isChecked) {
      preguntaControl.setValue([...currentValues, option]);
    } else {
      preguntaControl.setValue(currentValues.filter((val) => val !== option));
    }
    preguntaControl.markAsTouched();
  }

  inicializarPreguntas() {
    const cantidad = this.evaluationForm.get('cantidad_preguntas')?.value || 10;
    this.numPreguntas = cantidad;

    for (let i = 0; i < this.numPreguntas; i++) {
      this.preguntasArray.push(new FormControl('', [Validators.required]));
    }
  }

  loadUserData(): void {
    const userData = localStorage.getItem('user');
    if (userData) {
      this.user = JSON.parse(userData);
      this.id_rol = this.user?.id_rol ?? null;
      this.id_area_usuario = this.user?.id_area ?? null;
    }
  }

  loadAreas() {
    this.http.get(environment.apiUrl + `/area`).subscribe(
      (response: any) => (this.areas = response),
      (error) => {
        console.error('Error al obtener las areas:', error);
      }
    );
  }

  async handleFileInput(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target?.files?.length) return;

    try {
      const files = Array.from(target.files);

      // Validar cantidad de archivos
      if (files.length < this.minFiles || files.length > this.maxFiles) {
        throw new Error(
          `Debe seleccionar entre ${this.minFiles} y ${this.maxFiles} imágenes`
        );
      }

      // Validar nombres de archivos (deben ser números del 1 al 8)
      const invalidFiles = files.filter((file) => {
        const fileName = file.name.split('.')[0]; // Obtener nombre sin extensión
        const fileNumber = parseInt(fileName);
        return isNaN(fileNumber) || fileNumber < 1 || fileNumber > 8;
      });

      if (invalidFiles.length > 0) {
        throw new Error(
          'Los nombres de los archivos deben ser números del 1 al 8 (ej: 1.jpg, 2.png)'
        );
      }

      // Mostrar loader
      Swal.fire({
        title: 'Procesando...',
        html: 'Subiendo imágenes...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // 1. Primero crear la evaluación si no existe
      if (!this.id_evaluacion) {
        await this.crearEvaluacion();
      }

      // 2. Subir archivos en paralelo
      const uploadPromises = files.map((file) => this.uploadFile(file));
      await Promise.all(uploadPromises);

      // Éxito
      await Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Imágenes subidas correctamente',
      });

      this.mostrarInputImagenes = false;
      this.router.navigate(['/evaluaciones']);
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: this.getErrorMessage(error),
      });
    }
  }

  private async uploadFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('image', file);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const etapa = this.evaluationForm.get('etapa')?.value;
    const area = this.evaluationForm.get('id_area')?.value;
    const fileNameNumber = file.name.split('.')[0]; // Obtener el número del nombre del archivo

    // Nombre del archivo manteniendo el formato requerido
    const fileName = `${etapa}_${user.id_rol_usuario}_${area}_${this.id_evaluacion}_${fileNameNumber}`;

    // Subir archivo
    const response = await lastValueFrom(
      this.http.post<{ url: string }>(
        `${environment.apiUrl}/archivo/image?fileName=${fileName}`,
        formData
      )
    );

    const imageUrl = environment.serverUrl + response.url;

    // Registrar imagen en la evaluación
    await this.registerImage(imageUrl);
  }

  private async registerImage(imageUrl: string): Promise<void> {
    if (!this.id_evaluacion) {
      throw new Error('No hay ID de evaluación para registrar la imagen');
    }

    const payload = {
      link_imagen: imageUrl,
      id_evaluacion: this.id_evaluacion,
    };

    await lastValueFrom(
      this.http.post(`${environment.apiUrl}/imagen-evaluacion`, payload)
    );
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error.message || 'Error en el servidor';
    }
    return error instanceof Error ? error.message : 'Error desconocido';
  }

  private async crearEvaluacion(): Promise<void> {
    if (this.evaluationForm.invalid) {
      throw new Error('El formulario de evaluación no es válido');
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const id_rol_usuario = user?.id_rol_usuario;

    if (!id_rol_usuario) {
      throw new Error('No se pudo obtener el rol del usuario');
    }

    const preguntas: Pregunta[] = this.preguntasArray.controls.map((control) =>
      this.crearPreguntaAlternativas(control.value)
    );

    const payload = {
      nombre: this.evaluationForm.get('nombre')?.value,
      duracion: this.evaluationForm.get('duracion')?.value,
      etapa: this.evaluationForm.get('etapa')?.value,
      fecha_inicio: this.evaluationForm.get('fecha_inicio')?.value,
      fecha_fin: this.evaluationForm.get('fecha_fin')?.value,
      grado: this.evaluationForm.get('grado')?.value,
      nivel_educativo: this.evaluationForm.get('nivel_educativo')?.value,
      id_rol_usuario,
      id_area: this.evaluationForm.get('id_area')?.value,
      preguntas,
    };

    const response = await lastValueFrom(
      this.http.post<{ id_evaluacion: number }>(
        `${environment.apiUrl}/evaluacion`,
        payload
      )
    );

    this.id_evaluacion = response.id_evaluacion.toString();
  }

  crearPreguntaAlternativas(respuestaSeleccionada: string): Pregunta {
    const alternativas: PreguntaAlternativa[] = [
      { correcta: respuestaSeleccionada === 'A' ? 1 : 0 },
      { correcta: respuestaSeleccionada === 'B' ? 1 : 0 },
      { correcta: respuestaSeleccionada === 'C' ? 1 : 0 },
      { correcta: respuestaSeleccionada === 'D' ? 1 : 0 },
    ];

    return { alternativas };
  }

  async onSubmit() {
    if (this.evaluationForm.invalid) {
      await Swal.fire(
        'Advertencia',
        'Por favor, complete todos los campos.',
        'warning'
      );
      return;
    }

    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: '¿Quieres crear la evaluación?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si, crear',
      cancelButtonText: 'No, cancelar',
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Procesando...',
      text: 'Creando evaluación...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const id_rol_usuario = user?.id_rol_usuario;

      if (!id_rol_usuario) {
        throw new Error('No se pudo obtener el rol del usuario');
      }

      const preguntas: Pregunta[] = this.preguntasArray.controls.map(
        (control) => this.crearPreguntaAlternativas(control.value)
      );

      const payload = {
        nombre: this.evaluationForm.get('nombre')?.value,
        duracion: this.evaluationForm.get('duracion')?.value,
        etapa: this.evaluationForm.get('etapa')?.value,
        fecha_inicio: this.evaluationForm.get('fecha_inicio')?.value,
        fecha_fin: this.evaluationForm.get('fecha_fin')?.value,
        grado: this.evaluationForm.get('grado')?.value,
        nivel_educativo: this.evaluationForm.get('nivel_educativo')?.value,
        id_rol_usuario,
        id_area:
          this.id_rol === '3'
            ? this.id_area_usuario
            : this.evaluationForm.get('id_area')?.value,
        preguntas,
      };

      const response = await lastValueFrom(
        this.http.post<{ id_evaluacion: number }>(
          `${environment.apiUrl}/evaluacion`,
          payload
        )
      );

      this.id_evaluacion = response.id_evaluacion.toString();

      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Evaluación creada con éxito',
      });

      this.mostrarInputImagenes = true;
      this.mostrarEvaluacion = false;
    } catch (error) {
      Swal.close();

      const errorMessage =
        error instanceof HttpErrorResponse
          ? error.error.message || 'Error al crear la evaluación'
          : 'Ocurrió un error inesperado';

      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });

      console.error('Error al crear la evaluación:', error);
    }
  }

  onBack() {
    if (this.isFormDirty && !confirm('¿Está seguro de salir?')) {
      return;
    }
    this.router.navigate(['/evaluaciones']);
  }

  abrirConvertidorPdf() {
    window.open(this.urlConvertidorPdf, '_blank');
  }

  actualizarNumeroPreguntas() {
    // Guardar las respuestas actuales
    const respuestasActuales: string[] = [];
    for (let i = 0; i < this.preguntasArray.length; i++) {
      respuestasActuales.push(this.preguntasArray.at(i).value);
    }

    const cantidad = this.evaluationForm.get('cantidad_preguntas')?.value || 10;
    this.numPreguntas = cantidad;

    // Limpiar el array actual
    while (this.preguntasArray.length) {
      this.preguntasArray.removeAt(0);
    }

    // Agregar nuevas preguntas y restaurar respuestas si existen
    for (let i = 0; i < this.numPreguntas; i++) {
      const respuesta = i < respuestasActuales.length ? respuestasActuales[i] : '';
      this.preguntasArray.push(new FormControl(respuesta, [Validators.required]));
    }
  }
}
